import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { compose } from 'utilities/functions/composition';
import {
  LabelDecorator,
  DescriptionDecorator,
} from 'components/common/form/decorators';
import { serializeClasses } from 'utilities/css';

const Button = ({
  children,
  type,
  leftAddon,
  rightAddon,
  className,
  size,
  ...rest
}) => {
  // this allows us to achieve the same effect without <ButtonContent />
  // which uses flex row and has a default css value of flex-wrap: nowrap
  // nowrap is only present when at least 1 addon prop is passed
  // otherwise default behavior which is wrap is still kept
  const classes = serializeClasses([
    className,
    size,
    (leftAddon || rightAddon) && 'nowrap',
  ]);

  return (
    // eslint-disable-next-line react/button-has-type
    <button className={classes} {...rest} type={type}>
      {leftAddon && <div className="left-addon">{leftAddon}</div>}
      {children}
      {rightAddon && <div className="right-addon">{rightAddon}</div>}
    </button>
  );
};

Button.propTypes = {
  size: PropTypes.oneOf(['small', 'large']),
  className: PropTypes.string,
  type: PropTypes.string,
  rightAddon: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  leftAddon: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  children: PropTypes.node,
};
Button.defaultProps = {
  size: 'small',
  className: undefined,
  type: 'button',
  rightAddon: undefined,
  leftAddon: undefined,
  children: undefined,
};

const ComposedButton = compose(LabelDecorator, DescriptionDecorator)(Button);

const ADDON_PADDING = '12px';
export const BaseButton = styled(ComposedButton)`
  padding: 8px 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  background-color: #00000000;
  border: none;
  border-radius: 4px;
  color: ${({ theme }) => theme.buttons.baseButton.default.font};
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  height: 32px;
  text-align: center;
  vertical-align: middle;
  /* @todo remove temp class until we roll out new widths holistically */
  &.wide {
    width: 332px;
  }
  &.default {
    min-width: 128px;
  }
  /* ********** */
  &.large {
    font-size: 16px;
    height: 40px;
  }
  &.inline {
    display: inline-flex;
  }
  &.nowrap {
    flex-wrap: nowrap;
    white-space: nowrap;
  }
  &.red {
    color: ${({ theme }) => theme.colors.errorRed}
  }
  &:hover {
    background-color: ${({ theme }) => theme.buttons.baseButton.hovered.background};
  }
  &:focus {
    background-color: ${({ theme }) => theme.buttons.baseButton.focused.background};
  }
  &:disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.buttons.baseButton.disabled.font};
    fill: ${({ theme }) => theme.buttons.baseButton.disabled.font};
  }
  .left-addon {
    display: flex;
    align-items: center;
    padding-right: ${ADDON_PADDING};
  }
  .right-addon {
    display: flex;
    align-items: center;
    padding-left: ${ADDON_PADDING};
  }
  &.teal {
    color: ${({ theme }) => theme.colors.brand03};
  }
  &.outline {
    border: 2px solid ${({ theme }) => theme.buttons.baseButton.default.font};
    line-height: 12px;
    :disabled {
      border: none
    }
  }
  &.text {
    padding: 0;
    min-width: unset;
    height: auto;
  }
  &.bold {
    font-weight: bold;
  }
`;

export const BaseButtonBlue = styled(BaseButton)`
  color: ${({ theme }) => theme.colors.text05};
  &.outline {
    border: 2px solid ${({ theme }) => theme.colors.text05};
    padding: 6px 16px;
    :disabled {
      border: none;
    }
  }
`;
export const BaseButtonTeal = styled(BaseButton)`
  color: ${({ theme }) => theme.colors.brand03};
  &.outline {
    border: 2px solid ${({ theme }) => theme.colors.brand03};
    padding: 6px 16px;
    :disabled {
      border: none;
    }
  }
`;

export default BaseButton;
